import React, {
  useMemo, useState, useEffect, useContext, useRef,
} from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Dropdown,
  Header,
  Segment,
  Table,
  Message,
  Label,
  Loader,
  Button,
  Modal,
} from 'semantic-ui-react';
import ProfileModal from '../../Experts/ProfileModal';
import CurrentEmploymentInfo from '../../Experts/CurrentEmploymentInfo';
import Job from '../../../models/Job';
import { getJobStatusColor } from '../../../utils/jobStatus';
import {
  PROJECT_EXPERT_RECOMMENDATIONS,
  PROJECT_EXPERT_EVALUATION_PAGE,
} from '../../../queries/project';
import { INVITE_EXPERT_TO_PROJECT } from '../../../queries/job';
import MessageContext from '../../../contexts/MessageContext';

const ALL_EXPERTS_VALUE = '__all_experts__';
const PER_PAGE = 20;

const verdictKey = (verdict) => String(verdict || '').trim().toLowerCase();

const getVerdictColor = (verdict) => {
  const normalized = verdictKey(verdict);
  if (normalized === 'very good fit') return 'teal';
  if (normalized === 'good fit') return 'green';
  if (normalized === 'borderline') return 'yellow';
  if (normalized === 'not enough context') return 'orange';
  if (normalized === 'not a fit') return 'red';
  return 'grey';
};

const isHiddenVerdict = (verdict) => {
  const normalized = verdictKey(verdict);
  return normalized === 'not enough context' || normalized === 'not a fit';
};

const isInviteRecommendedVerdict = (verdict) => {
  const normalized = verdictKey(verdict);
  return normalized === 'very good fit' || normalized === 'good fit';
};

const formatEvaluationText = (evaluationText) => {
  const lines = String(evaluationText || '').split('\n');
  const seenLines = {};

  return lines
    .filter((line) => {
      const normalized = line.trim().toLowerCase();
      if (normalized.startsWith('verdict:')) return false;
      if (normalized.startsWith('score:')) return false;
      return true;
    })
    .map((line) => {
      const count = (seenLines[line] || 0) + 1;
      seenLines[line] = count;
      const lineKey = `${line}-${count}`;
      const normalized = line.trim().toLowerCase();
      if (normalized.startsWith('reasoning:')) {
        return (
          <div key={lineKey}>
            <strong>Reasoning:</strong>
          </div>
        );
      }
      if (normalized.startsWith('why not:') || normalized.startsWith('why-not:')) {
        return (
          <div key={lineKey}>
            <strong>Why not:</strong>
          </div>
        );
      }
      return <div key={lineKey}>{line}</div>;
    });
};

const ExpertRecommendations = ({ projectId }) => {
  const { setSuccessPopup, setErrorPopup } = useContext(MessageContext);

  const {
    data,
    loading,
    refetch,
  } = useQuery(PROJECT_EXPERT_RECOMMENDATIONS, {
    variables: { id: projectId },
    skip: !projectId,
  });
  const [inviteExpertToProject] = useMutation(INVITE_EXPERT_TO_PROJECT);

  const project = data?.project;
  const recommendations = project?.expertRecommendations || {};
  const criteria = recommendations.criteria || [];
  const evaluations = recommendations.evaluations || [];
  const jobs = project?.jobs || [];
  const { completedEvaluations, scheduledEvaluations } = useMemo(() => {
    const completed = [];
    const scheduled = [];

    evaluations.forEach((evaluation) => {
      if (!evaluation) return;
      if (evaluation.evaluationStatus === 'completed') completed.push(evaluation);
      if (evaluation.evaluationStatus === 'scheduled') scheduled.push(evaluation);
    });

    return { completedEvaluations: completed, scheduledEvaluations: scheduled };
  }, [evaluations]);
  const allExpertIds = useMemo(() => {
    const ids = new Set([
      ...(recommendations.uniqueExpertIds || []),
      ...criteria.flatMap((criterion) => criterion.expertIds || []),
      ...completedEvaluations.map((evaluation) => evaluation.expertId),
    ]);

    return [...ids].filter(Boolean);
  }, [completedEvaluations, criteria, recommendations.uniqueExpertIds]);

  const categoryOptions = [
    {
      key: ALL_EXPERTS_VALUE,
      value: ALL_EXPERTS_VALUE,
      text: 'All experts',
    },
    ...criteria.map((criterion, index) => ({
      key: `${criterion.description || 'category'}-${index}`,
      value: criterion.description,
      text: criterion.description || `Category ${index + 1}`,
    })),
  ];

  const [selectedDescription, setSelectedDescription] = useState(
    ALL_EXPERTS_VALUE,
  );
  const [showNotFitEvaluations, setShowNotFitEvaluations] = useState(false);
  const [pageState, setPageState] = useState({ filterKey: null, page: 1 });
  const [pagedState, setPagedState] = useState({ filterKey: null, evaluations: [], total: 0 });
  const scrollSentinelRef = useRef(null);
  const [isInvitingRecommendedExperts, setIsInvitingRecommendedExperts] = useState(false);
  const [isBulkInviteConfirmOpen, setIsBulkInviteConfirmOpen] = useState(false);

  useEffect(() => {
    if (!categoryOptions.some((option) => option.value === selectedDescription)) {
      setSelectedDescription(ALL_EXPERTS_VALUE);
    }
  }, [categoryOptions, selectedDescription]);

  // Paging state is tagged with the filters it belongs to, so changing the
  // category or the hidden-verdict toggle starts a fresh list from page 1 in
  // the same render rather than requesting a stale page for the new filters.
  const filterKey = `${selectedDescription}|${showNotFitEvaluations}`;
  const page = pageState.filterKey === filterKey ? pageState.page : 1;
  const pagedEvaluations = pagedState.filterKey === filterKey ? pagedState.evaluations : [];
  const pagedTotal = pagedState.filterKey === filterKey ? pagedState.total : 0;

  const {
    data: pageData,
    loading: pageLoading,
  } = useQuery(PROJECT_EXPERT_EVALUATION_PAGE, {
    variables: {
      id: projectId,
      criterion: selectedDescription === ALL_EXPERTS_VALUE ? null : selectedDescription,
      includeHidden: showNotFitEvaluations,
      pagination: { page, per_page: PER_PAGE },
    },
    skip: !projectId,
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    const evaluationPage = pageData?.project?.expertRecommendations?.evaluationPage;
    if (!evaluationPage) return;
    setPagedState((prev) => {
      const previousEvaluations = prev.filterKey === filterKey ? prev.evaluations : [];
      const evaluationsById = new Map(
        previousEvaluations.map((evaluation) => [evaluation.expertId, evaluation]),
      );
      evaluationPage.evaluations.forEach((evaluation) => {
        evaluationsById.set(evaluation.expertId, evaluation);
      });
      return {
        filterKey,
        evaluations: Array.from(evaluationsById.values()),
        total: evaluationPage.total,
      };
    });
  }, [pageData]);

  const hasMoreEvaluations = pagedEvaluations.length < pagedTotal;

  // Infinite scroll: request the next page once the sentinel below the table
  // scrolls into view. Pages arrive already sorted by score from the server.
  useEffect(() => {
    const sentinel = scrollSentinelRef.current;
    if (!sentinel || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMoreEvaluations && !pageLoading) {
        setPageState({ filterKey, page: page + 1 });
      }
    }, { rootMargin: '150px' });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [filterKey, page, hasMoreEvaluations, pageLoading, loading]);

  const selectedCriterion = useMemo(
    () => (selectedDescription === ALL_EXPERTS_VALUE ? null : (
      criteria.find((criterion) => criterion.description === selectedDescription)
      || criteria[0]
    )),
    [criteria, selectedDescription],
  );

  const evaluationsByExpertId = useMemo(
    () => evaluations.reduce((acc, evaluation) => {
      acc[evaluation.expertId] = evaluation;
      return acc;
    }, {}),
    [evaluations],
  );

  const jobStatusByExpertId = useMemo(
    () => jobs.reduce((acc, job) => {
      const expertId = job?.user?.id;
      if (!expertId) return acc;
      const normalizedJob = new Job(job);
      acc[expertId] = normalizedJob.status;
      return acc;
    }, {}),
    [jobs],
  );

  const isAllExpertsSelected = selectedDescription === ALL_EXPERTS_VALUE;
  const selectedExpertIds = isAllExpertsSelected
    ? allExpertIds
    : (selectedCriterion?.expertIds || []);
  const matchedEvaluations = isAllExpertsSelected
    ? completedEvaluations
    : selectedExpertIds
      .map((expertId) => evaluationsByExpertId[expertId])
      .filter((evaluation) => evaluation && evaluation.evaluationStatus === 'completed');
  const evaluatedExpertsCount = matchedEvaluations.length;
  const scheduledExpertsCount = isAllExpertsSelected
    ? scheduledEvaluations.length
    : selectedExpertIds
      .filter((expertId) => evaluationsByExpertId[expertId]?.evaluationStatus === 'scheduled')
      .length;

  const notFitEvaluationsCount = matchedEvaluations.filter(
    (evaluation) => isHiddenVerdict(evaluation.verdict),
  ).length;
  const visibleEvaluationsCount = showNotFitEvaluations
    ? evaluatedExpertsCount
    : evaluatedExpertsCount - notFitEvaluationsCount;

  const recommendedEvaluations = (() => {
    const evaluationsById = {};
    matchedEvaluations.forEach((evaluation) => {
      if (!evaluation?.expertId || !isInviteRecommendedVerdict(evaluation.verdict)) return;
      if (!evaluationsById[evaluation.expertId]) {
        evaluationsById[evaluation.expertId] = evaluation;
      }
    });
    return Object.values(evaluationsById);
  })();

  const alreadyInvitedRecommendedCount = recommendedEvaluations
    .filter((evaluation) => jobStatusByExpertId[evaluation.expertId]).length;

  const inviteableRecommendedEvaluations = recommendedEvaluations
    .filter((evaluation) => !jobStatusByExpertId[evaluation.expertId]);

  const handleInviteRecommendedExperts = async () => {
    const inviteCount = inviteableRecommendedEvaluations.length;
    if (!projectId || inviteCount === 0 || isInvitingRecommendedExperts) return;

    setIsInvitingRecommendedExperts(true);

    try {
      const results = await Promise.allSettled(
        inviteableRecommendedEvaluations.map((evaluation) => inviteExpertToProject({
          variables: {
            userId: evaluation.expertId,
            projectId,
          },
        })),
      );

      const invitedCount = results.filter((result) => result.status === 'fulfilled').length;
      const failedResults = results.filter((result) => result.status === 'rejected');
      const failedCount = failedResults.length;
      const firstFailureMessage = failedResults[0]?.reason?.message;

      await refetch();

      if (invitedCount > 0 && failedCount === 0) {
        setSuccessPopup({
          title: `Sent ${invitedCount} invite${invitedCount === 1 ? '' : 's'}`,
          content: alreadyInvitedRecommendedCount > 0
            ? `Skipped ${alreadyInvitedRecommendedCount} expert${alreadyInvitedRecommendedCount === 1 ? '' : 's'} who were already invited.`
            : 'All selected experts were invited successfully.',
        });
        return;
      }

      if (invitedCount > 0 && failedCount > 0) {
        setSuccessPopup({
          title: `Sent ${invitedCount} invite${invitedCount === 1 ? '' : 's'}`,
          content: `${failedCount} invite${failedCount === 1 ? '' : 's'} could not be sent.${alreadyInvitedRecommendedCount > 0 ? ` Skipped ${alreadyInvitedRecommendedCount} already invited expert${alreadyInvitedRecommendedCount === 1 ? '' : 's'}.` : ''}`,
        });
        return;
      }

      setErrorPopup({
        title: 'Unable to send invites',
        header: 'No invites were sent',
        content: firstFailureMessage || 'Please try again.',
      });
    } catch (error) {
      setErrorPopup({
        title: 'Unable to send invites',
        header: 'Bulk invite failed',
        content: error?.message || 'Please try again.',
      });
    } finally {
      setIsInvitingRecommendedExperts(false);
      setIsBulkInviteConfirmOpen(false);
    }
  };

  if (loading) {
    return (
      <Segment>
        <Header as="h3">Expert Recommendations</Header>
        <Loader active inline="centered" content="Loading recommendations..." />
      </Segment>
    );
  }

  if (!criteria.length && evaluations.length === 0) {
    return (
      <Segment>
        <Header as="h3">Expert Recommendations</Header>
        <Message info content="No expert categories are available for this project yet." />
      </Segment>
    );
  }

  return (
    <Segment id="project-expert-recommendations">
      <Header as="h3">Expert Recommendations</Header>
      <p>
        Toggle by expert category to review the experts matched to that category and their
        evaluation details.
      </p>

      <Dropdown
        fluid
        selection
        options={categoryOptions}
        value={selectedDescription}
        onChange={(e, { value }) => setSelectedDescription(value)}
        placeholder="Select an expert category"
      />

      <div style={{ marginTop: '12px', marginBottom: '12px' }}>
        <Label>
          Experts Evaluated:
          {' '}
          {evaluatedExpertsCount}
        </Label>
        {scheduledExpertsCount > 0 && (
          <Label color="blue" style={{ marginLeft: '8px' }}>
            Evaluations in progress:
            {' '}
            {scheduledExpertsCount}
          </Label>
        )}
        <Label style={{ marginLeft: '8px' }}>
          Recommended (Very good fit + Good fit):
          {' '}
          {recommendedEvaluations.length}
        </Label>
        {alreadyInvitedRecommendedCount > 0 && (
          <Label color="grey" style={{ marginLeft: '8px' }}>
            Already invited:
            {' '}
            {alreadyInvitedRecommendedCount}
          </Label>
        )}
        <Button
          primary
          style={{ marginLeft: '12px' }}
          onClick={() => setIsBulkInviteConfirmOpen(true)}
          loading={isInvitingRecommendedExperts}
          disabled={isInvitingRecommendedExperts || inviteableRecommendedEvaluations.length === 0}
          content={`Invite recommended (${inviteableRecommendedEvaluations.length})`}
        />
      </div>

      {scheduledExpertsCount > 0 && (
        <Message info style={{ marginTop: 0 }}>
          {isAllExpertsSelected
            ? `${scheduledExpertsCount} expert evaluation${scheduledExpertsCount === 1 ? '' : 's'} ${scheduledExpertsCount === 1 ? 'is' : 'are'} currently in progress.`
            : `${scheduledExpertsCount} expert evaluation${scheduledExpertsCount === 1 ? '' : 's'} in this category ${scheduledExpertsCount === 1 ? 'is' : 'are'} currently in progress.`}
        </Message>
      )}

      <Modal
        size="tiny"
        open={isBulkInviteConfirmOpen}
        onClose={() => setIsBulkInviteConfirmOpen(false)}
      >
        <Modal.Header>Invite Recommended Experts</Modal.Header>
        <Modal.Content>
          {`Invite ${inviteableRecommendedEvaluations.length} ${inviteableRecommendedEvaluations.length === 1 ? 'expert' : 'experts'} with Very good fit or Good fit verdicts in this selection?`}
          {alreadyInvitedRecommendedCount > 0 && (
            <p style={{ marginTop: '8px' }}>
              {`Already invited experts (${alreadyInvitedRecommendedCount}) will be skipped.`}
            </p>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button
            onClick={() => setIsBulkInviteConfirmOpen(false)}
            disabled={isInvitingRecommendedExperts}
          >
            Cancel
          </Button>
          <Button
            primary
            onClick={handleInviteRecommendedExperts}
            loading={isInvitingRecommendedExperts}
            disabled={isInvitingRecommendedExperts || inviteableRecommendedEvaluations.length === 0}
            content={`Invite ${inviteableRecommendedEvaluations.length}`}
          />
        </Modal.Actions>
      </Modal>

      {visibleEvaluationsCount === 0 ? (
        <Message
          warning
          content={isAllExpertsSelected
            ? 'No completed evaluations are available yet.'
            : 'No completed evaluations are available yet for experts in this category.'}
        />
      ) : (
        <Table celled stackable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell width={3}>Expert</Table.HeaderCell>
              <Table.HeaderCell width={1}>Verdict</Table.HeaderCell>
              <Table.HeaderCell width={12}>Evaluation</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {pagedEvaluations.map((evaluation) => (
              <Table.Row key={evaluation.expertId}>
                <Table.Cell>
                  <ProfileModal expertId={evaluation.expertId}>
                    <span className="span-link">{evaluation.expertName || evaluation.expertId}</span>
                  </ProfileModal>
                  {(evaluation.expertTitle || evaluation.expertOrganizationName) && (
                    <CurrentEmploymentInfo>
                      <div style={{ color: 'rgba(0, 0, 0, 0.6)', marginTop: '2px' }}>
                        {[evaluation.expertTitle, evaluation.expertOrganizationName]
                          .filter(Boolean)
                          .join(', ')}
                      </div>
                    </CurrentEmploymentInfo>
                  )}
                  {jobStatusByExpertId[evaluation.expertId] && (
                    <div style={{ marginTop: '4px' }}>
                      <Label circular size="mini" color={getJobStatusColor(jobStatusByExpertId[evaluation.expertId])} empty />
                      {' '}
                      {jobStatusByExpertId[evaluation.expertId]}
                    </div>
                  )}
                </Table.Cell>
                <Table.Cell>
                  <Label color={getVerdictColor(evaluation.verdict)}>
                    {evaluation.verdict || 'N/A'}
                  </Label>
                </Table.Cell>
                <Table.Cell style={{ whiteSpace: 'pre-wrap' }}>
                  {evaluation.evaluation
                    ? formatEvaluationText(evaluation.evaluation)
                    : 'No evaluation text available'}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      <div ref={scrollSentinelRef} />
      {pageLoading && (
        <Loader active inline="centered" style={{ marginTop: '12px' }} />
      )}

      {!showNotFitEvaluations && !hasMoreEvaluations && notFitEvaluationsCount > 0 && (
        <div style={{ marginTop: '12px' }}>
          <a
            href="#show-not-fit"
            onClick={(e) => {
              e.preventDefault();
              setShowNotFitEvaluations(true);
            }}
          >
            {`Show ${notFitEvaluationsCount} hidden evaluations`}
          </a>
        </div>
      )}
    </Segment>
  );
};

export default ExpertRecommendations;
